// V1.0.65: Modal xem lịch sử thay đổi của 1 dự án.
// Tương tự DocumentAccessLogModal nhưng query qua /projects/{id}/audit-log.
import { useState } from 'react';
import { Modal, Table, Tag, Spin, Empty, Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { History, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { apiClient } from '@/services/apiClient';

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  onClose: () => void;
}

interface AuditLogItem {
  id: number;
  timestamp: string;
  userId: string | null;
  username: string | null;
  ip: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityDescription: string | null;
  result: string;
  failureReason: string | null;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  CREATE_PROJECT:     { label: 'Tạo dự án',         color: 'green' },
  UPDATE_PROJECT:     { label: 'Sửa dự án',         color: 'blue' },
  ARCHIVE_PROJECT:    { label: 'Lưu trữ',           color: 'gold' },
  DELETE_PROJECT:     { label: 'Xóa dự án',         color: 'red' },
  ADD_MEMBER:         { label: 'Thêm thành viên',   color: 'cyan' },
  UPDATE_MEMBER_ROLE: { label: 'Đổi vai trò',       color: 'purple' },
  REMOVE_MEMBER:      { label: 'Xóa thành viên',    color: 'orange' },
};

export function ProjectAuditLogModal({ open, projectId, projectName, onClose }: Props) {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['project-audit-log', projectId, page],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: AuditLogItem[]; total: number; page: number; pageSize: number }>(
        `/projects/${projectId}/audit-log`, { params: { page, pageSize } }
      );
      return data;
    },
    enabled: open && !!projectId,
  });

  const logs = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Người thực hiện',
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: (v: string | null, row: AuditLogItem) => v ?? (row.userId ? row.userId.slice(0, 8) : '—'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (v: string) => {
        const cfg = ACTION_LABELS[v] ?? { label: v, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Chi tiết',
      dataIndex: 'entityDescription',
      key: 'detail',
      render: (v: string | null, row: AuditLogItem) => {
        if (row.result === 'FAILURE') {
          return (
            <span>
              <Tag color="red">FAILED</Tag>
              {v ?? row.failureReason ?? '—'}
            </span>
          );
        }
        return v || '—';
      },
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 130,
      render: (v: string | null) => v || '—',
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="refresh" icon={<RefreshCw size={13} />} onClick={() => refetch()}>
          Làm mới
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>Đóng</Button>,
      ]}
      title={
        <span>
          <History size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
          Lịch sử thay đổi — {projectName}
        </span>
      }
      width={1000}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : logs.length === 0 ? (
        <Empty description="Chưa có lịch sử thay đổi" />
      ) : (
        <Table<AuditLogItem>
          columns={columns as any}
          dataSource={logs}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `Tổng ${t} bản ghi`,
          }}
          scroll={{ y: 480 }}
        />
      )}
    </Modal>
  );
}
